import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Paper from 'material-ui/Paper'
import RaisedButton from 'material-ui/RaisedButton'
import FlatButton from 'material-ui/FlatButton'
import TextField from 'material-ui/TextField'
import SelectField from 'material-ui/SelectField'
import MenuItem from 'material-ui/MenuItem'
import Toggle from 'material-ui/Toggle'
import Dialog from 'material-ui/Dialog'
import { setSnackbarMessage } from '../../../actions/snackbar'
import api from '../../../api'

class SessionSaleIntakeQuestions extends Component<any, any> {
  constructor (props) {
    super(props)
    this.state = {
      questions: [],
      loading: true,
      saving: false,
      showAddCustomDialog: false,
      disableAllQuestions: false,
      customQuestion: {
        question_text: '',
        question_type: 'text',
        storage_table: 'session_reservations',
        storage_path: '',
        options: null
      },
      newOption: {
        value: '',
        name: ''
      },
      editingOptions: {}, // Track which questions are being edited
      questionOptions: {}, // Track option changes for each question
      showDeleteDialog: false,
      questionToDelete: null,
      deleting: false
    }
  }

  componentDidMount () {
    this.loadQuestions()
  }

  loadQuestions = async () => {
    try {
      const { sessionSaleId } = this.props.params
      const response = await api.get(`admin/session_sales/${sessionSaleId}/intake_questions`)

      // Parse the JSON response
      const responseData = await response.json()
      const questions = responseData.data || []

      // Initialize questionOptions state with current options
      const questionOptions = {}
      if (Array.isArray(questions)) {
        questions.forEach(q => {
          if (q.options) {
            questionOptions[q.id] = Array.isArray(q.options) ? [...q.options] : []
          }
        })
      }

      this.setState({
        questions: Array.isArray(questions) ? questions : [],
        loading: false,
        disableAllQuestions: !!questions?.every(q => q.is_enabled === false),
        questionOptions
      })
    } catch (error) {
      console.error('Error loading intake questions:', error)

      // Check if it's a 404 (endpoint not implemented) or other API error
      if (error.response && error.response.status === 404) {
        this.props.setSnackbarMessage('Intake questions API not yet implemented. Please contact your developer to implement the backend API endpoints.')
      } else {
        this.props.setSnackbarMessage('Error loading intake questions. The API may not be available.')
      }

      this.setState({
        questions: [],
        loading: false
      })
    }
  }

  handleQuestionToggle = (questionId, isEnabled) => {
    // Don't allow individual toggles when all questions are disabled
    if (this.state.disableAllQuestions) {
      return
    }

    this.setState(prevState => ({
      questions: prevState.questions.map(q =>
        q.id === questionId ? { ...q, is_enabled: isEnabled } : q
      )
    }))
  }

  handleDisableAllToggle = (isDisabled) => {
    this.setState(prevState => ({
      disableAllQuestions: isDisabled,
      questions: prevState.questions.map(q => ({
        ...q,
        is_enabled: !isDisabled
      }))
    }))
  }

  handleCustomTextChange = (questionId, customText) => {
    this.setState(prevState => ({
      questions: prevState.questions.map(q =>
        q.id === questionId ? { ...q, custom_question_text: customText } : q
      )
    }))
  }

  handleSortOrderChange = (questionId, sortOrder) => {
    this.setState(prevState => ({
      questions: prevState.questions.map(q =>
        q.id === questionId ? { ...q, session_sort_order: parseInt(sortOrder) || 0 } : q
      )
    }))
  }

  handleRequiredToggle = (questionId, isRequired) => {
    this.setState(prevState => ({
      questions: prevState.questions.map(q =>
        q.id === questionId ? { ...q, is_required: isRequired } : q
      )
    }))
  }

  saveQuestions = async () => {
    try {
      this.setState({ saving: true })
      const { sessionSaleId } = this.props.params

      const questionsData = this.state.questions.map((q, index) => ({
        intake_question_id: q.id,
        is_enabled: q.is_enabled || false,
        custom_question_text: q.custom_question_text || null,
        sort_order: q.session_sort_order !== undefined && q.session_sort_order !== null ? q.session_sort_order : (q.sort_order || (index + 1)),
        custom_options: this.state.questionOptions[q.id] || null,
        is_required: q.is_required !== undefined ? q.is_required : true
      }))

      const res = await api.put(`admin/session_sales/${sessionSaleId}/intake_questions`, {
        body: {
          questions: questionsData
        }
      })

      if (!res.ok) {
        this.props.setSnackbarMessage('Failed to update intake questions')
      } else {
        this.props.setSnackbarMessage('Intake questions updated successfully')
      }
    } catch (error) {
      console.error('Error saving intake questions:', error)

      if (error.response && error.response.status === 404) {
        this.props.setSnackbarMessage('Save endpoint not yet implemented. Please contact your developer to implement the backend API.')
      } else {
        this.props.setSnackbarMessage('Error saving intake questions. The API may not be available.')
      }
    } finally {
      this.setState({ saving: false })
    }
  }

  handleAddCustomQuestion = () => {
    this.setState({ showAddCustomDialog: true })
  }

  handleCustomQuestionChange = (field, value) => {
    this.setState(prevState => {
      const updatedQuestion = {
        ...prevState.customQuestion,
        [field]: value
      }

      // Auto-generate storage path when question text changes
      if (field === 'question_text') {
        // Create a safe key from the question text
        const safeKey = value
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .trim() // Remove leading/trailing whitespace
          .substring(0, 100) // Limit length

        // Only set storage path if we have a valid safe key
        if (safeKey) {
          updatedQuestion.storage_path = `metadata.${safeKey}`
          updatedQuestion.storage_table = 'session_reservations'
        } else {
          // Clear storage path if question text doesn't produce a valid key
          updatedQuestion.storage_path = ''
        }
      }

      return {
        customQuestion: updatedQuestion
      }
    })
  }

  handleNewOptionChange = (field, value) => {
    this.setState(prevState => ({
      newOption: {
        ...prevState.newOption,
        [field]: value
      }
    }))
  }

  addOption = () => {
    const { newOption, customQuestion } = this.state

    if (!newOption.value.trim() || !newOption.name.trim()) {
      this.props.setSnackbarMessage('Both option value and name are required')
      return
    }

    const currentOptions = customQuestion.options || []
    const newOptions = [...currentOptions, { value: newOption.value, name: newOption.name }]

    this.handleCustomQuestionChange('options', newOptions)
    this.setState({
      newOption: {
        value: '',
        name: ''
      }
    })
  }

  removeOption = (indexToRemove) => {
    const { customQuestion } = this.state
    const currentOptions = customQuestion.options || []
    const newOptions = currentOptions.filter((_, index) => index !== indexToRemove)

    this.handleCustomQuestionChange('options', newOptions.length > 0 ? newOptions : null)
  }

  // Methods for editing existing question options
  toggleEditOptions = (questionId) => {
    this.setState(prevState => ({
      editingOptions: {
        ...prevState.editingOptions,
        [questionId]: !prevState.editingOptions[questionId]
      }
    }))
  }

  handleQuestionOptionChange = (questionId, optionIndex, field, value) => {
    this.setState(prevState => {
      const currentOptions = [...(prevState.questionOptions[questionId] || [])]
      if (currentOptions[optionIndex]) {
        currentOptions[optionIndex] = {
          ...currentOptions[optionIndex],
          [field]: value
        }
      }
      return {
        questionOptions: {
          ...prevState.questionOptions,
          [questionId]: currentOptions
        }
      }
    })
  }

  addQuestionOption = (questionId) => {
    this.setState(prevState => {
      const currentOptions = [...(prevState.questionOptions[questionId] || [])]
      currentOptions.push({ name: '', value: '' })
      return {
        questionOptions: {
          ...prevState.questionOptions,
          [questionId]: currentOptions
        }
      }
    })
  }

  removeQuestionOption = (questionId, optionIndex) => {
    this.setState(prevState => {
      const currentOptions = [...(prevState.questionOptions[questionId] || [])]
      currentOptions.splice(optionIndex, 1)
      return {
        questionOptions: {
          ...prevState.questionOptions,
          [questionId]: currentOptions
        }
      }
    })
  }

  resetQuestionOptions = (questionId, originalOptions) => {
    this.setState(prevState => ({
      questionOptions: {
        ...prevState.questionOptions,
        [questionId]: Array.isArray(originalOptions) ? [...originalOptions] : []
      },
      editingOptions: {
        ...prevState.editingOptions,
        [questionId]: false
      }
    }))
  }

  submitCustomQuestion = async () => {
    try {
      const { sessionSaleId } = this.props.params
      const { customQuestion, questions } = this.state

      if (!customQuestion.question_text.trim()) {
        this.props.setSnackbarMessage('Question text is required')
        return
      }

      // Generate storage path if it's empty (happens when question text had no valid characters initially)
      if (!customQuestion.storage_path.trim()) {
        const safeKey = customQuestion.question_text
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .trim() // Remove leading/trailing whitespace
          .substring(0, 100) // Limit length

        if (!safeKey) {
          this.props.setSnackbarMessage('Question text must contain at least one letter or number')
          return
        }

        // Update the storage path before submitting
        customQuestion.storage_path = `metadata.${safeKey}`
      }

      // Calculate the next sort order by finding the highest current sort order and adding 1
      const maxSortOrder = Math.max(0, ...questions.map(q => q.sort_order || 0))
      const nextSortOrder = maxSortOrder + 1

      const questionDataWithSortOrder = {
        ...customQuestion,
        sort_order: nextSortOrder
      }

      const res = await api.post(`admin/session_sales/${sessionSaleId}/intake_questions/custom`, {
        body: questionDataWithSortOrder
      })
      if (!res.ok) {
        this.props.setSnackbarMessage('Failed to add custom question')
        return
      }

      this.props.setSnackbarMessage('Custom question added successfully')
      this.setState({
        showAddCustomDialog: false,
        customQuestion: {
          question_text: '',
          question_type: 'text',
          storage_table: 'session_reservations',
          storage_path: '',
          options: null
        },
        newOption: {
          value: '',
          name: ''
        }
      })
      this.loadQuestions()
    } catch (error) {
      console.error('Error adding custom question:', error)

      if (error.response && error.response.status === 404) {
        this.props.setSnackbarMessage('Add custom question endpoint not yet implemented. Please contact your developer to implement the backend API.')
      } else {
        this.props.setSnackbarMessage('Error adding custom question. The API may not be available.')
      }
    }
  }

  handleDeleteQuestion = (question) => {
    this.setState({
      showDeleteDialog: true,
      questionToDelete: question
    })
  }

  confirmDeleteQuestion = async () => {
    try {
      this.setState({ deleting: true })
      const { sessionSaleId } = this.props.params
      const { questionToDelete } = this.state

      const res = await api.del(`admin/session_sales/${sessionSaleId}/intake_questions/${questionToDelete.id}`)

      if (!res.ok) {
        this.props.setSnackbarMessage('Failed to delete custom question')
        return
      }

      this.props.setSnackbarMessage('Custom question deleted successfully')
      this.setState({
        showDeleteDialog: false,
        questionToDelete: null
      })
      this.loadQuestions()
    } catch (error) {
      console.error('Error deleting custom question:', error)

      if (error.response && error.response.status === 404) {
        this.props.setSnackbarMessage('Delete endpoint not yet implemented. Please contact your developer to implement the backend API.')
      } else {
        this.props.setSnackbarMessage('Error deleting custom question. Unexpected API error.')
      }
    } finally {
      this.setState({ deleting: false })
    }
  }

  cancelDeleteQuestion = () => {
    this.setState({
      showDeleteDialog: false,
      questionToDelete: null
    })
  }

  renderQuestionOptions = (question) => {
    if (!question.options) return null

    const isEditingOptions = this.state.editingOptions[question.id]
    const currentOptions = this.state.questionOptions[question.id] || question.options
    const isGrayedOut = this.state.disableAllQuestions
    const isEnabled = question.is_enabled !== undefined ? question.is_enabled : (question.is_default || false)

    if (isEditingOptions) {
      return (
        <div style={{ marginTop: 16, marginLeft: 20, border: '1px solid #ddd', padding: 16, borderRadius: 4, backgroundColor: '#f9f9f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong>Edit Options:</strong>
            <div>
              <FlatButton
                label='Cancel'
                onClick={() => this.resetQuestionOptions(question.id, question.options)}
                style={{ marginRight: 8 }}
              />
              <FlatButton
                label='Done'
                primary
                onClick={() => this.toggleEditOptions(question.id)}
              />
            </div>
          </div>

          {currentOptions && currentOptions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {currentOptions.map((option, index) => (
                <div key={index} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                  <TextField
                    floatingLabelText='Name'
                    value={option.name ?? ''}
                    onChange={(event) => this.handleQuestionOptionChange(question.id, index, 'name', event.target.value)}
                    style={{ flex: 1 }}
                  />
                  <TextField
                    floatingLabelText='Value'
                    value={option.value ?? ''}
                    onChange={(event) => this.handleQuestionOptionChange(question.id, index, 'value', event.target.value)}
                    style={{ flex: 1 }}
                  />
                  <FlatButton
                    label='Remove'
                    secondary
                    onClick={() => this.removeQuestionOption(question.id, index)}
                    style={{ minWidth: 'auto' }}
                  />
                </div>
              ))}
            </div>
          )}

          <RaisedButton
            label='Add Option'
            primary
            onClick={() => this.addQuestionOption(question.id)}
            style={{ marginTop: 8 }}
          />
        </div>
      )
    }

    if (Array.isArray(question.options) || (this.state.questionOptions[question.id] && this.state.questionOptions[question.id].length > 0)) {
      // Use updated options from state if available, otherwise fall back to original options
      const displayOptions = this.state.questionOptions[question.id] && this.state.questionOptions[question.id].length > 0
        ? this.state.questionOptions[question.id]
        : question.options

      return (
        <div style={{ marginTop: 8, marginLeft: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Options:</strong>
            {(question.question_type === 'select' || question.question_type === 'checkbox') && question.enable_options_edit !== false && (
              <FlatButton
                label='Edit Options'
                primary
                onClick={() => this.toggleEditOptions(question.id)}
                disabled={!isEnabled || isGrayedOut}
                style={{ minWidth: 'auto', marginLeft: 8 }}
              />
            )}
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {displayOptions.map((option, index) => (
              <li key={index}>{option.name || option.value || option}</li>
            ))}
          </ul>
        </div>
      )
    }

    return null
  }

  renderQuestion = (question, index) => {
    // Default questions should be enabled by default, custom questions use their is_enabled value
    const isEnabled = question.is_enabled
    const customText = question.custom_question_text || ''
    const sortOrder = question.session_sort_order !== undefined && question.session_sort_order !== null ? question.session_sort_order : (question.sort_order || (index + 1))
    const isRequired = question.is_required !== undefined ? question.is_required : true

    // Apply gray-out styling when all questions are disabled
    const isGrayedOut = this.state.disableAllQuestions
    const paperStyle = {
      padding: 16,
      marginBottom: 16,
      opacity: isGrayedOut ? 0.5 : 1,
      backgroundColor: isGrayedOut ? '#f5f5f5' : '#fff'
    }

    return (
      <Paper key={question.id} style={paperStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Original Text:</strong> {question.question_text}
            </div>

            <div style={{ marginBottom: 8 }}>
              <strong>Type:</strong> {question.question_type}
              {question.is_default && <span style={{ marginLeft: 8, color: '#666' }}>(Default Question)</span>}
              {!question.is_default && <span style={{ marginLeft: 8, color: '#2196F3' }}>(Custom Question)</span>}
              {isRequired && <span style={{ marginLeft: 8, color: '#f44336', fontWeight: 'bold' }}>(Required)</span>}
              {!isRequired && <span style={{ marginLeft: 8, color: '#4caf50' }}>(Optional)</span>}
            </div>

            {this.renderQuestionOptions(question)}

            <div style={{ marginTop: 16, display: 'flex', gap: 16, alignItems: 'flex-end' }}>
              <TextField
                floatingLabelText='Custom Question Text (optional)'
                value={customText}
                onChange={(event) => this.handleCustomTextChange(question.id, event.target.value)}
                disabled={!isEnabled || isGrayedOut}
                multiLine
                rows={2}
                style={{ flex: 1 }}
              />

              <TextField
                floatingLabelText='Sort Order'
                value={sortOrder}
                onChange={(event) => this.handleSortOrderChange(question.id, event.target.value)}
                disabled={!isEnabled || isGrayedOut}
                type='number'
                style={{ width: 100 }}
                onWheel={(event) => event.target.blur()}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Enabled</label>
              <Toggle
                toggled={isEnabled}
                onToggle={(event, toggled) => this.handleQuestionToggle(question.id, toggled)}
                disabled={isGrayedOut}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Required</label>
              <Toggle
                toggled={isRequired}
                onToggle={(event, toggled) => this.handleRequiredToggle(question.id, toggled)}
                disabled={!isEnabled || isGrayedOut}
              />
            </div>
            {!question.is_default && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 8 }}>
                <FlatButton
                  label='Delete'
                  secondary
                  onClick={() => this.handleDeleteQuestion(question)}
                  disabled={isGrayedOut}
                  style={{ minWidth: 'auto', fontSize: 10 }}
                />
              </div>
            )}
          </div>
        </div>
      </Paper>
    )
  }

  render () {
    const { questions, loading, saving, showAddCustomDialog, customQuestion, showDeleteDialog, questionToDelete, deleting } = this.state

    if (loading) {
      return <div style={{ padding: 16 }}>Loading intake questions...</div>
    }

    const customQuestionActions = [
      <FlatButton
        label='Cancel'
        primary={false}
        onClick={() => this.setState({ showAddCustomDialog: false })}
      />,
      <FlatButton
        label='Add Question'
        primary
        onClick={this.submitCustomQuestion}
      />
    ]

    const deleteQuestionActions = [
      <FlatButton
        label='Cancel'
        primary={false}
        onClick={this.cancelDeleteQuestion}
        disabled={deleting}
      />,
      <FlatButton
        label={deleting ? 'Deleting...' : 'Delete'}
        secondary
        onClick={this.confirmDeleteQuestion}
        disabled={deleting}
      />
    ]

    return (
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2>Configure Intake Questions</h2>
          <div>
            <RaisedButton
              label='Add Custom Question'
              secondary
              onClick={this.handleAddCustomQuestion}
              style={{ marginRight: 8 }}
            />
            <RaisedButton
              label={saving ? 'Saving...' : 'Save Changes'}
              primary
              onClick={this.saveQuestions}
              disabled={saving}
            />
          </div>
        </div>

        <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ flexShrink: 0 }}>
              <strong>Disable All</strong>
            </div>
            <Toggle
              toggled={this.state.disableAllQuestions}
              onToggle={(event, toggled) => this.handleDisableAllToggle(toggled)}
              style={{ marginLeft: 16 }}
            />
          </div>
        </Paper>

        <div>
          {questions.map((question, index) => this.renderQuestion(question, index))}
        </div>

        {questions.length === 0 && (
          <Paper style={{ padding: 32, textAlign: 'center' }}>
            <p>No intake questions available. Default questions may need to be seeded in the database.</p>
          </Paper>
        )}

        <Dialog
          title='Add Custom Question'
          actions={customQuestionActions}
          modal={false}
          open={showAddCustomDialog}
          onRequestClose={() => this.setState({ showAddCustomDialog: false })}
          contentStyle={{ width: '90%', maxWidth: 600 }}
        >
          <TextField
            floatingLabelText='Question Text'
            value={customQuestion.question_text}
            onChange={(event) => this.handleCustomQuestionChange('question_text', event.target.value)}
            fullWidth
            multiLine
            rows={2}
            style={{ marginBottom: 16 }}
          />

          <SelectField
            floatingLabelText='Question Type'
            value={customQuestion.question_type}
            onChange={(event, index, value) => this.handleCustomQuestionChange('question_type', value)}
            fullWidth
            style={{ marginBottom: 16 }}
          >
            <MenuItem value='text' primaryText='Text' />
            <MenuItem value='number' primaryText='Number' />
            <MenuItem value='select' primaryText='Select (Single Choice)' />
            <MenuItem value='checkbox' primaryText='Checkbox (Multiple Choice)' />
          </SelectField>

          {(customQuestion.question_type === 'select' || customQuestion.question_type === 'checkbox') && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ marginBottom: 16 }}>Question Options</h4>

              {/* Current Options List */}
              {customQuestion.options && customQuestion.options.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <strong>Current Options:</strong>
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    {customQuestion.options.map((option, index) => (
                      <li key={index} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
                          <span style={{ flex: 1 }}>{option.name}</span>
                          <span style={{ flex: 1 }}>{option.value}</span>
                        </div>
                        <FlatButton
                          label='Remove'
                          secondary
                          onClick={() => this.removeOption(index)}
                          style={{ minWidth: 'auto', marginLeft: 8 }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Add New Option Form */}
              <div style={{ border: '1px solid #ddd', padding: 16, borderRadius: 4, backgroundColor: '#f9f9f9' }}>
                <strong style={{ display: 'block', marginBottom: 12 }}>Add New Option:</strong>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <TextField
                    floatingLabelText='Name'
                    value={this.state.newOption.name}
                    onChange={(event) => this.handleNewOptionChange('name', event.target.value)}
                    hintText='Option 1'
                    style={{ flex: 1 }}
                  />
                  <TextField
                    floatingLabelText='Value'
                    value={this.state.newOption.value}
                    onChange={(event) => this.handleNewOptionChange('value', event.target.value)}
                    hintText='value1'
                    style={{ flex: 1 }}
                  />
                  <RaisedButton
                    label='Add Option'
                    primary
                    onClick={this.addOption}
                    style={{ marginBottom: 8 }}
                  />
                </div>
              </div>
            </div>
          )}
        </Dialog>

        <Dialog
          title='Delete Custom Question'
          actions={deleteQuestionActions}
          modal
          open={showDeleteDialog}
          onRequestClose={this.cancelDeleteQuestion}
        >
          {questionToDelete && (
            <div>
              <p>Are you sure you want to delete this custom question?</p>
              <div style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4, marginTop: 16 }}>
                <strong>Question:</strong> {questionToDelete.question_text}
                <br />
                <strong>Type:</strong> {questionToDelete.question_type}
              </div>
            </div>
          )}
        </Dialog>
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => bindActionCreators({ setSnackbarMessage }, dispatch)

// @ts-ignore
export default connect(null, mapDispatchToProps)(SessionSaleIntakeQuestions)
